import React, { useState } from "react";

import { CopyOutlined } from "@ant-design/icons";
import { Button, Col, Input, Modal, Row, Space, Table } from "antd";
import { toast } from "utils/toast";

import { AdvisorApiData, ApiData } from "js/components/Organization/types";

import {
  useRegenerateExpressToken,
  useRegenerateMultireportToken,
  useRegenerateSicToken,
  useRevokeExpressToken,
} from "../../../hooks/useAccounts";

interface DataType {
  data: ApiData;
  apiId: string;
}

const ApiTabContent = ({ data, apiId }: DataType) => {
  const [isRegenerateModalVisible, setRegenerateModalVisible] = useState(false);
  const [isRevokeModalVisible, setRevokeModalVisible] = useState(false);
  const [showNewTokenModal, setShowNewTokenModal] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | null>(
    null,
  );

  // TanStack Query mutation hooks
  const regenerateExpressMutation = useRegenerateExpressToken();
  const regenerateSicMutation = useRegenerateSicToken();
  const regenerateMultireportMutation = useRegenerateMultireportToken();
  const revokeExpressMutation = useRevokeExpressToken();

  const handleRegenerateCancel = () => {
    setRegenerateModalVisible(false);
    setShowNewTokenModal(false);
  };

  const handleRevokeCancel = () => {
    setRevokeModalVisible(false);
  };

  const handleRegenerateConfirm = () => {
    if (!selectedAdvisorId) return;

    // Select the appropriate mutation based on apiId
    let mutation;
    switch (apiId) {
      case "express":
        mutation = regenerateExpressMutation;
        break;
      case "sic":
        mutation = regenerateSicMutation;
        break;
      case "multireport":
        mutation = regenerateMultireportMutation;
        break;
      default:
        toast.error("API desconocida");
        return;
    }

    mutation.mutate(selectedAdvisorId, {
      onSuccess: (responseData: any) => {
        // Display the new token in modal (hooks already show success message)
        setNewToken(responseData.token);
        setShowNewTokenModal(true);
        // Update local data display (optional, hook invalidates cache)
        const advisor = data.users.find(
          (advisor) => advisor.id == selectedAdvisorId,
        );
        if (advisor) {
          advisor.token = responseData.token.slice(-5);
        }
      },
    });
  };

  const handleRevokeConfirm = () => {
    if (!selectedAdvisorId) return;

    revokeExpressMutation.mutate(selectedAdvisorId, {
      onSuccess: () => {
        // Update local data display (optional, hook invalidates cache)
        const advisor = data.users.find(
          (advisor) => advisor.id == selectedAdvisorId,
        );
        if (advisor) {
          advisor.token = "";
        }
        setRevokeModalVisible(false);
      },
    });
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(newToken);
    toast.success("Token copiado");
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success("Url copiada");
  };

  const handleRegenerateOk = () => {
    setRegenerateModalVisible(false);
    setShowNewTokenModal(false);
  };
  const handleShowRegenerateModal = (advisorId: string) => {
    setSelectedAdvisorId(advisorId);
    setRegenerateModalVisible(true);
  };

  const handleShowRevokeModal = (advisorId: string) => {
    setSelectedAdvisorId(advisorId);
    setRevokeModalVisible(true);
  };

  const hideToken = (token: string) => `**********${token}`;

  const tableData = data.users.map((item) => ({
    ...item,
    token: item.token ? hideToken(item.token) : item.token,
  }));

  // Get loading state based on current apiId
  const getRegenerateLoading = () => {
    switch (apiId) {
      case "express":
        return regenerateExpressMutation.isPending;
      case "sic":
        return regenerateSicMutation.isPending;
      case "multireport":
        return regenerateMultireportMutation.isPending;
      default:
        return false;
    }
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (text: string) => <p>{text}</p>,
    },
    {
      title: "Token",
      dataIndex: "token",
      key: "token",
    },
    {
      title: "Actions",
      key: "action",
      render: (record: AdvisorApiData) => (
        <Space size="middle">
          <Button
            type="primary"
            ghost
            onClick={() => handleShowRegenerateModal(record.id)}
          >
            Regenerar
          </Button>
          {apiId === "express" && (
            <Button
              danger
              ghost
              onClick={() => handleShowRevokeModal(record.id)}
            >
              Revocar
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const style: React.CSSProperties = {
    // background: "#0092ff",
    padding: "8px 0",
  };

  return (
    <>
      <Row>
        <Col>
          <p>Url:</p>
        </Col>
      </Row>
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col
          xs={24}
          sm={18}
          md={18}
          lg={12}
          xl={9}
          className="gutter-row"
          span={18}
        >
          <div style={style}>
            <Input
              id="api-url"
              value={data.url}
              readOnly
              className="custom-input"
              addonAfter={
                <CopyOutlined
                  onClick={() => {
                    handleCopyUrl(data.url);
                  }}
                />
              }
            />
          </div>
        </Col>
        <Col xs={24} sm={24} md={24} lg={9} xl={9} span={18}>
          <div style={style}>
            <Button
              type="primary"
              ghost
              onClick={() => {
                window.open(data.docs, "_blank");
              }}
            >
              Ver documentación
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div style={style}>
            <Table
              columns={columns}
              dataSource={tableData}
              rowKey="id"
              pagination={false}
            />
          </div>
        </Col>
      </Row>

      <Modal
        title="Regenerar Token"
        open={isRegenerateModalVisible}
        onCancel={handleRegenerateCancel}
        footer={
          showNewTokenModal
            ? [
                <Button key="ok" type="primary" onClick={handleRegenerateOk}>
                  Listo
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleRegenerateCancel}>
                  Cancelar
                </Button>,
                <Button
                  key="confirm"
                  type="primary"
                  loading={getRegenerateLoading()}
                  onClick={handleRegenerateConfirm}
                >
                  Confirmar
                </Button>,
              ]
        }
      >
        {showNewTokenModal ? (
          <>
            <p>
              Asegurate de copiar y mantener el token en un lugar seguro,{" "}
              <strong>esta será la única vez que se mostrará</strong>.
            </p>
            <Input
              value={newToken}
              addonAfter={<CopyOutlined onClick={handleCopyToken} />}
            />
          </>
        ) : (
          <>
            <p>¿Estás seguro de regenerar el token? </p>
            <p>
              El token actual será reemplazado al dar click en el botón
              "Confirmar".
            </p>
          </>
        )}
      </Modal>

      <Modal
        title="Revocar Token"
        open={isRevokeModalVisible}
        onCancel={handleRevokeCancel}
        footer={[
          <Button key="cancel" onClick={handleRevokeCancel}>
            Cancelar
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={revokeExpressMutation.isPending}
            onClick={handleRevokeConfirm}
          >
            Confirmar
          </Button>,
        ]}
      >
        <>
          <p>¿Estás seguro de revocar el token? </p>
        </>
      </Modal>
    </>
  );
};

export default ApiTabContent;
